/* ============================================================
   Bytescale uploader — shared helper
   ------------------------------------------------------------
   Single integration point for file uploads across the theme.
   Replaces the previous Uploadcare integration. To swap the
   upload provider in the future, only this file needs to change
   (plus the <script> tag that loads the provider SDK in
   snippets/bytescale-uploader-loader.liquid).

   Markup contract (set by the Liquid snippets that use this):
     A wrapper element containing:
       <div class="image-preview-single">
         <img ... />                      <- preview thumbnail (images only)
         <input type="hidden"
                data-bytescale-uploader   <- marks this input as an uploader
                data-bytescale-label="…"  <- button text
                name="properties[…]"       <- where the fileUrl is stored
                value="">
       </div>
     Optional:
       <span data-bytescale-filename></span>  <- shows uploaded file name
       <div  data-bytescale-trigger></div>    <- custom click target (else img/preview)

   Public API (window.BytescaleUploader):
     init(root)            re-scan `root` (or document) for [data-bytescale-uploader]
     open(input)           open the widget for a given hidden input
     hasValue(input)       true if a file URL has been stored
     clear(input)          clear the stored value + preview
   ============================================================ */
(function () {
  'use strict';

  /* ---- Provider configuration (the ONLY provider-specific block) ---- */
  var CONFIG = {
    apiKey: 'public_223k2mWAavvbTsn2DBo9dJPSpwrv',
    maxFileCount: 1,
    // Allowed types: JPG, JPEG, PNG, PDF, SVG
    mimeTypes: ['image/jpeg', 'image/png', 'application/pdf', 'image/svg+xml'],
    extensions: ['.jpg', '.jpeg', '.png', '.pdf', '.svg'],
    maxFileSizeBytes: 15 * 1024 * 1024, // 15 MB
    locale: { 'uploadImage.label': 'Upload File' }
  };

  var IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.svg'];

  function isImageUrl(url) {
    if (!url) return false;
    var clean = url.split('?')[0].toLowerCase();
    for (var i = 0; i < IMAGE_EXTS.length; i++) {
      if (clean.indexOf(IMAGE_EXTS[i], clean.length - IMAGE_EXTS[i].length) !== -1) return true;
    }
    return false;
  }

  function fileNameFromUrl(url) {
    if (!url) return '';
    try {
      var path = url.split('?')[0];
      var parts = path.split('/');
      return decodeURIComponent(parts[parts.length - 1] || '');
    } catch (e) {
      return '';
    }
  }

  /* ---- DOM helpers (vanilla; no jQuery dependency) ---- */
  function closestWrapper(input) {
    return (
      input.closest('.image-preview-single') ||
      input.closest('[data-bytescale-wrapper]') ||
      input.parentElement
    );
  }

  function getPreviewImg(input) {
    var wrap = closestWrapper(input);
    return wrap ? wrap.querySelector('img') : null;
  }

  function getFilenameEl(input) {
    var wrap = closestWrapper(input);
    if (!wrap) return null;
    // search the wrapper, then its parent block
    return (
      wrap.querySelector('[data-bytescale-filename]') ||
      (wrap.parentElement && wrap.parentElement.querySelector('[data-bytescale-filename]'))
    );
  }

  function setPreview(input, url) {
    var img = getPreviewImg(input);
    if (img) {
      if (url && isImageUrl(url)) {
        img.setAttribute('src', url);
        img.style.visibility = 'visible';
        img.style.width = 'auto';
      } else {
        img.setAttribute('src', '');
        img.style.visibility = 'hidden';
        img.style.width = '0px';
      }
    }
    var nameEl = getFilenameEl(input);
    if (nameEl) nameEl.textContent = url ? fileNameFromUrl(url) : '';
  }

  /* ---- Validation of the selected file (client-side) ----
     Bytescale v4 returns an object like:
       { fileUrl, filePath, originalFile: { name, size, type }, size, mime, ... }
     Field names vary between SDK versions, so read defensively and derive
     the extension from the URL/path when no name is present. Bytescale has
     already enforced CONFIG.mimeTypes at upload time, so we only REJECT when
     we can positively identify a disallowed type — never on missing info. */
  function validateFile(file) {
    if (!file) return 'No file selected.';

    var orig = file.originalFile || {};
    var name = (
      file.originalFileName || file.fileName || file.name ||
      orig.name || orig.originalFileName || ''
    ).toLowerCase();
    // fall back to the extension in the stored URL / path
    if (!name) name = (file.fileUrl || file.filePath || '').split('?')[0].toLowerCase();

    var mime = (file.mime || file.mimeType || orig.type || orig.mime || '').toLowerCase();
    var size = file.size || file.fileSize || orig.size || 0;

    var extOk = CONFIG.extensions.some(function (ext) {
      return name.indexOf(ext, name.length - ext.length) !== -1;
    });
    var mimeOk = mime ? CONFIG.mimeTypes.indexOf(mime) !== -1 : false;

    // Only reject when we have identifying info AND it is not allowed.
    var haveInfo = !!name || !!mime;
    if (haveInfo && !extOk && !mimeOk) {
      return 'Unsupported file type. Please upload a JPG, PNG, PDF or SVG.';
    }
    if (size && size > CONFIG.maxFileSizeBytes) {
      return 'File is too large. Maximum size is 15 MB.';
    }
    return null;
  }

  /* ---- Store / clear the resulting URL on the hidden input ---- */
  function storeValue(input, url) {
    input.value = url || '';
    if (input.disabled && url) input.disabled = false;
    setPreview(input, url);
    renderState(input);
    // notify listeners (e.g. add-to-cart gating) that this input changed
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new CustomEvent('bytescale:change', {
      bubbles: true,
      detail: { url: url || '' }
    }));
  }

  /* ---- Toggle the empty (Upload) vs uploaded (Remove / Change) UI ---- */
  function renderState(input) {
    var wrap = closestWrapper(input);
    if (!wrap) return;
    var hasFile = !!input.value;
    var uploadBtn = wrap.querySelector('[data-bytescale-trigger]');
    var actions = wrap.querySelector('[data-bytescale-actions]');
    if (uploadBtn) uploadBtn.style.display = hasFile ? 'none' : '';
    if (actions) actions.style.display = hasFile ? '' : 'none';
  }

  function clearValue(input) {
    storeValue(input, '');
  }

  /* ---- Upload a chosen File straight to Bytescale (no widget UI) ----
     Uses the UploadManager API so clicking the button opens the native OS
     file browser directly (like the previous Uploadcare "direct" flow),
     skipping the Bytescale dropzone overlay. */
  function uploadFile(input, file) {
    if (typeof window.Bytescale === 'undefined' || !window.Bytescale.UploadManager) {
      console.error('[bytescale] SDK not loaded yet.');
      return;
    }

    // client-side pre-check on the native File (name/type/size)
    var preErr = validateFile({
      name: file.name,
      mime: file.type,
      size: file.size
    });
    if (preErr) {
      window.alert(preErr);
      return;
    }

    var trigger = getTrigger(input);
    var prevLabel = trigger ? trigger.textContent : '';
    if (trigger) {
      trigger.disabled = true;
      trigger.textContent = 'Uploading…';
    }

    var manager = new window.Bytescale.UploadManager({ apiKey: CONFIG.apiKey });
    manager.upload({ data: file, mime: file.type, originalFileName: file.name })
      .then(function (result) {
        var err = validateFile(result);
        if (err) {
          window.alert(err);
          return;
        }
        storeValue(input, result.fileUrl);
      })
      .catch(function (e) {
        console.error('[bytescale] upload failed:', e);
        window.alert('Upload failed. Please try again.');
      })
      .then(function () {
        if (trigger) {
          trigger.disabled = false;
          trigger.textContent = prevLabel;
        }
      });
  }

  /* ---- Open the native file browser for a given input ---- */
  function openFor(input) {
    var picker = getNativePicker(input);
    if (!picker) {
      console.error('[bytescale] no file picker available.');
      return;
    }
    picker.value = ''; // allow re-selecting the same file
    picker.click();
  }

  /* ---- Find the visible trigger button for an input ---- */
  function getTrigger(input) {
    var wrap = closestWrapper(input);
    return wrap ? wrap.querySelector('[data-bytescale-trigger]') : null;
  }

  /* ---- Find the hidden native file picker for an input ---- */
  function getNativePicker(input) {
    if (input.__bytescalePicker) return input.__bytescalePicker;
    var wrap = closestWrapper(input);
    return wrap ? wrap.querySelector('[data-bytescale-native-picker]') : null;
  }

  /* ---- Wire up a single uploader input ---- */
  function bindInput(input) {
    if (input.__bytescaleBound) return;
    input.__bytescaleBound = true;

    var wrap = closestWrapper(input);
    var label = input.getAttribute('data-bytescale-label') || 'Upload File';

    // Hidden native file picker — clicking the button opens the OS browser
    // directly, so the user never sees the Bytescale dropzone overlay.
    var picker = wrap ? wrap.querySelector('[data-bytescale-native-picker]') : null;
    if (!picker && wrap) {
      picker = document.createElement('input');
      picker.type = 'file';
      picker.setAttribute('data-bytescale-native-picker', '');
      picker.accept = CONFIG.extensions.concat(CONFIG.mimeTypes).join(',');
      picker.style.display = 'none';
      wrap.appendChild(picker);
    }
    if (picker) {
      input.__bytescalePicker = picker;
      picker.addEventListener('change', function () {
        if (picker.files && picker.files.length) {
          uploadFile(input, picker.files[0]);
        }
      });
    }

    // Build a visible trigger button inside the wrapper (keeps old look/feel)
    var trigger = wrap ? wrap.querySelector('[data-bytescale-trigger]') : null;
    if (!trigger && wrap) {
      trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'bytescale-upload-btn';
      trigger.setAttribute('data-bytescale-trigger', '');
      trigger.textContent = label;
      wrap.appendChild(trigger);
    }
    if (trigger) {
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        openFor(input);
      });
    }

    // Build the uploaded-state actions (Remove + Change), hidden until a
    // file exists. Mirrors the previous provider's uploaded-state UI.
    var actions = wrap ? wrap.querySelector('[data-bytescale-actions]') : null;
    if (!actions && wrap) {
      actions = document.createElement('div');
      actions.className = 'bytescale-actions';
      actions.setAttribute('data-bytescale-actions', '');
      actions.style.display = 'none';

      var removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'bytescale-remove-btn';
      removeBtn.setAttribute('data-bytescale-remove', '');
      removeBtn.textContent = 'Remove';
      removeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        clearValue(input);
      });

      var changeBtn = document.createElement('button');
      changeBtn.type = 'button';
      changeBtn.className = 'bytescale-change-btn';
      changeBtn.setAttribute('data-bytescale-change', '');
      changeBtn.textContent = 'Change';
      changeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openFor(input);
      });

      actions.appendChild(removeBtn);
      actions.appendChild(changeBtn);
      wrap.appendChild(actions);
    }

    // restore preview + correct state if a value already exists
    if (input.value) setPreview(input, input.value);
    renderState(input);
  }

  function init(root) {
    var scope = root || document;
    var inputs = scope.querySelectorAll('input[data-bytescale-uploader]');
    for (var i = 0; i < inputs.length; i++) bindInput(inputs[i]);
  }

  /* ---- Public API ---- */
  window.BytescaleUploader = {
    init: init,
    open: openFor,
    hasValue: function (input) { return !!(input && input.value); },
    clear: clearValue,
    config: CONFIG
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
})();
